import {
  type Account,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {getInferenceSub, useAccountPrivileges} from '../accounts/pricing';
import {useRampFlagInferenceCatalog} from '../rampFeatureFlags/rampFeatureFlags';
import {
  formatOverageChargeText,
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
  ZERO_COST_TEXT,
} from './utils';

/**
 * Hook to get inference usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetInferenceUsageDetails(selectedAccount: Account | null) {
  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {
    privileges: {inferenceLimitDateInterval, inferenceLimits: privilegeLimits},
  } = privilegeResults;
  const skip =
    selectedAccount?.name == null ||
    selectedAccount?.accountType === AccountType.Personal;
  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount.name},
    skip,
  });

  const stripeSubscription = getInferenceSub(
    billingPeriodResults.data?.organization ?? undefined
  );

  const enterpriseInferenceSubscription =
    billingPeriodResults.data?.organization?.inferenceEnterpriseSubscription;

  const isUsagePaid =
    stripeSubscription != null || enterpriseInferenceSubscription != null;

  const enterpriseLimitDateInterval =
    enterpriseInferenceSubscription != null
      ? {
          start: new Date(enterpriseInferenceSubscription.billingPeriodStart),
          end: new Date(enterpriseInferenceSubscription.billingPeriodEnd),
        }
      : undefined;

  const limits = {
    ...privilegeLimits,
  };
  const enterpriseFreeLimit =
    enterpriseInferenceSubscription?.inferenceLimit != null
      ? Number(BigInt(enterpriseInferenceSubscription?.inferenceLimit))
      : undefined;

  if (enterpriseFreeLimit != null) {
    limits.inferenceFreeLimit = enterpriseFreeLimit;
  }

  const finalLimitDateInterval =
    enterpriseLimitDateInterval ?? inferenceLimitDateInterval;

  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(finalLimitDateInterval)
    : (getIntervalMarkersFromSubscription(stripeSubscription) ??
      getIntervalMarkersFromCurrentCalendarMonth());
  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.InferenceCost,
    usagePeriod,
    skip
  );

  const results = [billingPeriodResults, privilegeResults, usageResults];

  const loading = results.some(result => result.loading);
  const error = results.find(result => result?.error)?.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage} = usageResults;
  const enabled = useRampFlagInferenceCatalog(selectedAccount?.name ?? '');

  // Calculate cost text
  let costText: string | undefined;
  let spend: number | undefined;
  if (isUsagePaid) {
    spend = 0;
    costText = ZERO_COST_TEXT;
    if (usage > limits.inferenceFreeLimit) {
      spend = (usage - limits.inferenceFreeLimit) / 100;
      costText = formatOverageChargeText(spend);
    }
  }

  return {
    billingPeriodStart,
    billingPeriodEnd,
    isUsagePaid,
    // TODO(dha-beep): This approach should be refactored (described below)
    // `isBillingStatusLoading` exists because `isUsagePaid` returns a
    // "confidently wrong" `false` while the billing query is still loading,
    // which briefly misclassifies a paying org as a trial (the trial-UI flash).
    // Exposing this separate flag lets consumers know when `isUsagePaid` is not
    // yet trustworthy.
    //
    // This is a deliberate short-term fix, and it's a footgun: the paid status
    // and its loading state are two independent fields, so anyone adding a
    // query-backed `isUsagePaid` (e.g. the sandboxes/training TODOs) must
    // remember to also update this flag and wire it into the consuming UI, or
    // the flash silently regresses.
    //
    // The plan right now is to wait for a refactor that involves both the backend and the frontend: have Go send
    // canonical, already-derived SKU values (billing period, usage, paid/trial
    // status, etc.) to the frontend via GraphQL, instead of the frontend
    // reconstructing them from raw subscription data and keeping that logic in
    // sync with Go. Once the backend owns that derivation, this loading-state
    // workaround (and the whole notion of the frontend guessing paid status)
    // goes away — so we're intentionally not investing in a frontend
    // abstraction here.
    isBillingStatusLoading: billingPeriodResults.loading,
    enabled,
    usage,
    limits,
    spend,
    costText,
    loading,
    error,
  };
}
